import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import NavbarBottom from './NavbarBottom';
import no_image_available from '../images/no_image_available.jpg';
import { useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Calendar, Newspaper } from 'lucide-react';

function News() {
    const params = useParams();
    const [newsList, SetNews] = useState([]);
    const [newsLink, SetNewsLink] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [isLoading, setIsLoading] = useState(false);

    const urlImg = `${process.env.REACT_APP_BASEURL}/berita/`;

    const handleSearchPageNext = async (page) => {
        if (page === currentPage || page < 1 || page > totalPages) return;

        setIsLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/news/?page=${page}`);
            const res = await response.json();
            SetNews(res.data.data);
            setCurrentPage(page);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error) {
            console.error('Error fetching news:', error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        const fetchNews = async () => {
            setIsLoading(true);
            try {
                const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/news`);
                const res = await response.json();
                SetNews(res.data.data);
                SetNewsLink(res.data.links);
                setCurrentPage(res.data.current_page || 1);
                setTotalPages(res.data.last_page || 1);
            } catch (error) {
                console.error('Error fetching news:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchNews();
    }, []);

    const getLang = localStorage.getItem('lang_code');

    // Format date
    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('id-ID', options);
    };

    // Generate pagination items
    const getPaginationItems = () => {
        const items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push({ label: '1', value: 1 });
            if (startPage > 2) items.push({ label: '...', value: null });
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push({ label: i.toString(), value: i });
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) items.push({ label: '...', value: null });
            items.push({ label: totalPages.toString(), value: totalPages });
        }

        return items;
    };

    return (
        <>
            <NavbarBottom />
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
                <div className="container max-w-7xl mx-auto">
                    {/* Header Section */}
                    <div className="mb-10 text-center">
                        <div className="flex items-center justify-center gap-3 mb-3">
                            <Newspaper className="w-8 h-8 text-[#0889c1]" />
                            <h1 className="text-4xl font-bold" style={{ fontFamily: 'MuseoSansRounded900' }}>
                                <span className="text-[#0889c1]">BERITA</span>
                                <span className="text-gray-700"> TERKINI</span>
                            </h1>
                        </div>
                        <div className="w-24 h-1 bg-gradient-to-r from-[#0889c1] to-purple-500 mx-auto rounded-full"></div>
                        <p className="mt-4 text-gray-600 text-sm font-medium">Informasi terbaru dan terkini seputar kami</p>
                    </div>

                    {/* Loading State */}
                    {isLoading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#0889c1]"></div>
                        </div>
                    ) : (
                        <>
                            {/* News Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                {newsList.map((data_news, indexnews_list) => (
                                    <div
                                        key={indexnews_list}
                                        className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                                    >
                                        <Link to={`/detail-berita/${data_news.id}/${data_news.link}`} className="block">
                                            {/* Image Container */}
                                            <div className="relative overflow-hidden h-56 bg-gray-100">
                                                {data_news?.images ? (
                                                    <img
                                                        src={urlImg + data_news.images}
                                                        alt={data_news.title}
                                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                                        loading="lazy"
                                                    />
                                                ) : (
                                                    <img
                                                        src={no_image_available}
                                                        alt="No image available"
                                                        className="w-full h-full object-cover"
                                                    />
                                                )}
                                                {/* Gradient Overlay */}
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                            </div>

                                            {/* Content */}
                                            <div className="p-5">
                                                {/* Date Badge */}
                                                {data_news.tgl && (
                                                    <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                                                        <Calendar className="w-3 h-3" />
                                                        <span>{formatDate(data_news.tgl)}</span>
                                                    </div>
                                                )}

                                                {/* Title */}
                                                <h3 className="font-bold text-gray-800 text-sm line-clamp-3 hover:text-[#0889c1] transition-colors duration-200"
                                                    style={{ fontFamily: 'MuseoSansRounded700' }}>
                                                    {data_news.title}
                                                </h3>

                                                {/* Read More Link */}
                                                <div className="mt-3 flex items-center text-[#0889c1] text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                    <span>Baca Selengkapnya</span>
                                                    <svg className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                                                    </svg>
                                                </div>
                                            </div>
                                        </Link>
                                    </div>
                                ))}
                            </div>

                            {/* Empty State */}
                            {newsList.length === 0 && !isLoading && (
                                <div className="text-center py-16">
                                    <Newspaper className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-500 text-lg">Belum ada berita tersedia</p>
                                </div>
                            )}

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="mt-12">
                                    <nav className="flex justify-center">
                                        <ul className="flex items-center gap-2" style={{ listStyleType: 'none' }}>
                                            {/* Previous Button */}
                                            <li>
                                                <button
                                                    onClick={() => handleSearchPageNext(currentPage - 1)}
                                                    disabled={currentPage === 1}
                                                    className={`flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200 ${currentPage === 1
                                                            ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                                                            : 'text-gray-700 bg-white hover:bg-[#0889c1] hover:text-white shadow-md hover:shadow-lg'
                                                        }`}
                                                >
                                                    <ChevronLeft className="w-5 h-5" />
                                                </button>
                                            </li>

                                            {/* Page Numbers */}
                                            {getPaginationItems().map((item, index) => (
                                                <li key={index}>
                                                    {item.value ? (
                                                        <button
                                                            onClick={() => handleSearchPageNext(item.value)}
                                                            className={`flex items-center justify-center min-w-[40px] h-10 px-3 rounded-lg transition-all duration-200 font-medium ${currentPage === item.value
                                                                    ? 'bg-[#0889c1] text-white shadow-lg shadow-[#0889c1]/30'
                                                                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md hover:shadow-lg'
                                                                }`}
                                                        >
                                                            {item.label}
                                                        </button>
                                                    ) : (
                                                        <span className="flex items-center justify-center w-10 h-10 text-gray-400">
                                                            {item.label}
                                                        </span>
                                                    )}
                                                </li>
                                            ))}

                                            {/* Next Button */}
                                            <li>
                                                <button
                                                    onClick={() => handleSearchPageNext(currentPage + 1)}
                                                    disabled={currentPage === totalPages}
                                                    className={`flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200 ${currentPage === totalPages
                                                            ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                                                            : 'text-gray-700 bg-white hover:bg-[#0889c1] hover:text-white shadow-md hover:shadow-lg'
                                                        }`}
                                                >
                                                    <ChevronRight className="w-5 h-5" />
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default News;